/**
 * #1041 — per-brand premade landing pages (MyCaseBuilder-style brand hubs).
 *
 * Category hubs already live at /premade-cases/gun-cases etc. Brand hubs target
 * "[brand] custom foam" queries that currently land on competitors.
 *
 * Slugs are static and must stay out of the build-slug namespace (verified by
 * collision tests). Server SEO + SPA known-child lists mirror these keys.
 */

export interface PremadeBrandPageConfig {
  /** URL slug under /premade-cases/:slug */
  slug: string;
  /** Exact caseBrand value as stored on catalog rows */
  brand: string;
  /** Document title (without site suffix) — client + shell SEO */
  title: string;
  headline: string;
  description: string;
  metaDescription: string;
  highlights: string[];
}

export const PREMADE_BRAND_PAGES: Record<string, PremadeBrandPageConfig> = {
  'pelican-cases': {
    slug: 'pelican-cases',
    brand: 'Pelican',
    title: 'Custom Foam Inserts for Pelican Cases',
    headline: 'Custom Foam Inserts for Pelican Cases',
    description:
      'Browse finished foam layouts cut for Pelican hard cases. Copy a build into CaseBuilder or start from your own gear list — manufacturing quotes included.',
    metaDescription:
      'Browse finished foam layouts cut for Pelican hard cases. Copy a build into CaseBuilder or design your own insert online.',
    highlights: [
      'Precision-cut polyethylene foam for Pelican shells',
      'Real customer builds you can copy as a starting point',
      'Design your own insert or request a quote from a stock layout',
    ],
  },
  'pelican-storm-cases': {
    slug: 'pelican-storm-cases',
    brand: 'Pelican Storm',
    title: 'Custom Foam Inserts for Pelican Storm Cases',
    headline: 'Custom Foam Inserts for Pelican Storm Cases',
    description:
      'Premade foam inserts sized for Pelican Storm cases. Browse shipped layouts or design your own in CaseBuilder.',
    metaDescription:
      'Premade foam inserts sized for Pelican Storm cases. Browse shipped layouts or design your own in CaseBuilder.',
    highlights: [
      'Storm-series case footprints with matching foam trays',
      'Multi-layer options where the build calls for them',
      'Export and quote path for CNC-ready production',
    ],
  },
  'nanuk-cases': {
    slug: 'nanuk-cases',
    brand: 'Nanuk',
    title: 'Custom Foam Inserts for Nanuk Cases',
    headline: 'Custom Foam Inserts for Nanuk Cases',
    description:
      'Finished foam layouts for Nanuk hard cases. Pick a premade build or open CaseBuilder with your Nanuk model.',
    metaDescription:
      'Finished foam layouts for Nanuk hard cases. Pick a premade build or open CaseBuilder with your Nanuk model.',
    highlights: [
      'Nanuk-compatible cutouts from real shipped designs',
      'Camera, drone, and tool kits represented in the catalog',
      'Self-serve design tool plus manufacturing quote',
    ],
  },
  'skb-cases': {
    slug: 'skb-cases',
    brand: 'SKB',
    title: 'Custom Foam Inserts for SKB Cases',
    headline: 'Custom Foam Inserts for SKB Cases',
    description:
      'Premade foam inserts for SKB cases — browse finished builds or design your own layout online.',
    metaDescription:
      'Premade foam inserts for SKB cases — browse finished builds or design your own layout online.',
    highlights: [
      'SKB i-Series and related footprints in the catalog',
      'Copy a layout in minutes or start from scratch',
      'CNC export and manufacturing quote from the same design',
    ],
  },
  'doro-cases': {
    slug: 'doro-cases',
    brand: 'Doro',
    title: 'Custom Foam Inserts for Doro Cases',
    headline: 'Custom Foam Inserts for Doro Cases',
    description:
      'Browse foam layouts cut for Doro cases, including pistol and range builds from the premade catalog.',
    metaDescription:
      'Browse foam layouts cut for Doro cases, including pistol and range builds from the premade catalog.',
    highlights: [
      'Doro Sport and related models with finished trays',
      'Gun and range layouts strongly represented',
      'Design your own or request a quote on a stock build',
    ],
  },
  'seahorse-cases': {
    slug: 'seahorse-cases',
    brand: 'Seahorse',
    title: 'Custom Foam Inserts for Seahorse Cases',
    headline: 'Custom Foam Inserts for Seahorse Cases',
    description:
      'Premade foam inserts for Seahorse protective cases. Start from a shipped layout or design your own.',
    metaDescription:
      'Premade foam inserts for Seahorse protective cases. Start from a shipped layout or design your own.',
    highlights: [
      'Seahorse footprints with precision-cut foam',
      'Browse builds by equipment or design from scratch',
      'Manufacturing quote path included',
    ],
  },
  'hprc-cases': {
    slug: 'hprc-cases',
    brand: 'HPRC',
    title: 'Custom Foam Inserts for HPRC Cases',
    headline: 'Custom Foam Inserts for HPRC Cases',
    description:
      'Finished foam layouts for HPRC hard cases — copy a build or open the designer with your case model.',
    metaDescription:
      'Finished foam layouts for HPRC hard cases — copy a build or open the designer with your case model.',
    highlights: [
      'HPRC-compatible inserts from the premade catalog',
      'Self-serve CaseBuilder plus quote flow',
      'CNC-ready export when you are ready to cut',
    ],
  },
  'apache-cases': {
    slug: 'apache-cases',
    brand: 'Apache',
    title: 'Custom Foam Inserts for Apache Cases',
    headline: 'Custom Foam Inserts for Apache Cases',
    description:
      'Premade foam inserts for Apache cases. Browse finished builds or design a custom tray online.',
    metaDescription:
      'Premade foam inserts for Apache cases. Browse finished builds or design a custom tray online.',
    highlights: [
      'Apache case footprints in the catalog',
      'Copy a layout or start from your gear list',
      'Quote and export from the same design',
    ],
  },
};

export function getPremadeBrandPage(slug: string): PremadeBrandPageConfig | undefined {
  const canonical = PREMADE_BRAND_PAGE_ALIASES[slug] ?? slug;
  return PREMADE_BRAND_PAGES[canonical];
}

/**
 * #1163 — short tokens users/search type without the `-cases` suffix.
 * Canonical slug stays `*-cases` for URLs we publish; PremadeCaseSlugPage redirects
 * an alias to its hub so the alias never renders as a second indexable copy.
 * Must equal server PREMADE_BRAND_SEO_ALIASES (premadeBrandAlias-1163.test.ts).
 */
export const PREMADE_BRAND_PAGE_ALIASES: Readonly<Record<string, string>> = {
  pelican: 'pelican-cases',
  'pelican-storm': 'pelican-storm-cases',
  nanuk: 'nanuk-cases',
  skb: 'skb-cases',
  doro: 'doro-cases',
  seahorse: 'seahorse-cases',
  hprc: 'hprc-cases',
  apache: 'apache-cases',
};

export const PREMADE_BRAND_PAGE_SLUGS = Object.keys(PREMADE_BRAND_PAGES);

/** Brand display name → URL slug (for chips / cross-links). */
export const BRAND_TO_SLUG: ReadonlyMap<string, string> = new Map(
  Object.values(PREMADE_BRAND_PAGES).map((p) => [p.brand, p.slug]),
);

export function getBrandSlug(brand: string): string | undefined {
  return BRAND_TO_SLUG.get(brand);
}
